import React, { Suspense, lazy, useEffect, useState } from "react";
import { setupFetchInterceptor } from "./services/fetchInterceptor";
import { Link, useNavigate } from "react-router-dom";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PlaybackProvider } from "./contexts/PlaybackContext";
import MiniPlayer from "./components/player/MiniPlayer";
import { SplashScreen } from "@capacitor/splash-screen";
import Login from "./pages/Login";
import Register from "./pages/Register";
import MobileLayout from "./components/MobileLayout";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AppStoreBanner from "./components/AppStoreBanner";
import UpdatePrompt from "./components/UpdatePrompt";
import { notificationManager } from "./services/notificationManager";
import {
  meditationService,
  DailyMeditationSummary,
  MeditationSeries,
} from "./services/meditationService";
import {
  ChatBubbleLeftRightIcon,
  SpeakerWaveIcon,
  ChatBubbleOvalLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  PencilIcon,
  SunIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";

const PasswordReset = lazy(() => import("./components/PasswordReset"));
const MeditationView = lazy(() => import("./pages/MeditationView"));
const ConfigureMeditation = lazy(() => import("./pages/ConfigureMeditation"));
const AiChat = lazy(() => import("./pages/AiChat"));
const About = lazy(() => import("./pages/About"));
const MediaLibrary = lazy(() => import("./pages/MediaLibrary"));
const IntroductoryMeditations = lazy(
  () => import("./pages/IntroductoryMeditations")
);
const QALibrary = lazy(() => import("./pages/QALibrary"));
const SavedRecordings = lazy(() => import("./pages/SavedRecordings"));
const SacredTextsSeries = lazy(() => import("./pages/SacredTextsSeries"));
const PoetryPage = lazy(() => import("./pages/PoetryPage"));
const MeditationLibrary = lazy(() => import("./pages/MeditationLibrary"));
const Publications = lazy(() => import("./pages/Publications"));
const PodcastSeries = lazy(() => import("./pages/PodcastSeries"));
const Feedback = lazy(() => import("./pages/Feedback"));
const FeedbackView = lazy(() => import("./pages/FeedbackView"));
const MeditationLoading = lazy(() => import("./pages/MeditationLoading"));
const Account = lazy(() => import("./pages/Account"));
const DailyRedirect = lazy(() => import("./pages/DailyRedirect"));
const SearchView = lazy(() => import("./pages/SearchView"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminMeditationsDashboard = lazy(
  () => import("./pages/AdminMeditationsDashboard")
);
const AdminMeditationDetail = lazy(
  () => import("./pages/AdminMeditationDetail")
);
const AdminMeditationEdit = lazy(() => import("./pages/AdminMeditationEdit"));
const AdminFeedbackInbox = lazy(() => import("./pages/AdminFeedbackInbox"));
const AdminUserDirectory = lazy(() => import("./pages/AdminUserDirectory"));
const AdminUserDetail = lazy(() => import("./pages/AdminUserDetail"));

// Protected Route component
/**
 * ProtectedRoute Component
 *
 * A wrapper component that ensures a route is only accessible to authenticated users.
 * If the user is not authenticated, they are redirected to the login page.
 *
 * @param children - The child components to render if authentication is successful
 * @returns The children if authenticated, otherwise redirects to login page
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Admin Route component
/**
 * AdminRoute Component
 *
 * A wrapper component that ensures a route is only accessible to admin users.
 * If the user is not authenticated, they are redirected to the login page.
 * If the user is authenticated but not an admin, they are redirected to the home page.
 *
 * @param children - The child components to render if admin access is granted
 * @returns The children if admin, otherwise redirects to login or home page
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!user.is_admin) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

const RouteLoadingFallback: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center px-6 text-center text-sm text-grey-600 dark:bg-grey-900 dark:text-grey-300">
    Loading...
  </div>
);

/**
 * SplashScreenHandler Component
 *
 * Handles showing and hiding the splash screen when the app is ready.
 * The native splash screen is automatically shown on app launch.
 * This component ensures it displays for a minimum duration and hides it once authentication loading is complete.
 */
const SplashScreenHandler: React.FC = () => {
  const { loading } = useAuth();
  const [, setSplashHidden] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    // Ensure splash screen is shown on Android
    const showSplash = async () => {
      try {
        await SplashScreen.show({
          showDuration: 3000,
          autoHide: false,
        });
      } catch (error) {
        // Ignore errors - splash may already be showing
        console.log("Splash screen show:", error);
      }
    };

    showSplash();

    // Wait for auth to finish loading and ensure minimum splash duration
    const hideSplash = async () => {
      // Wait for auth to load
      if (loading) {
        return;
      }

      // Ensure minimum splash screen display time (1 second)
      const minDisplayTime = 1000;
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      try {
        // Use configured fade out duration (3000ms)
        await SplashScreen.hide({
          fadeOutDuration: 3000,
        });
        setSplashHidden(true);
      } catch (error) {
        console.error("Error hiding splash screen:", error);
        // Still mark as hidden even if hide fails to prevent retries
        setSplashHidden(true);
      }
    };

    hideSplash();
  }, [loading, startTime]);

  return null;
};

interface MenuCardProps {
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  banner: string;
}

/**
 * MenuCard Component
 *
 * Renders a card-like UI element used in the main menu/home screen.
 * Each card represents a navigation option with a title, icon, and background image.
 *
 * @param onClick - Function to execute when the card is clicked
 * @param icon - React element to be displayed as the card icon
 * @param title - The main title text to display
 * @param subtitle - Secondary descriptive text
 * @param banner - Background image identifier
 * @returns A styled card button with the specified properties
 */
const MenuCard: React.FC<MenuCardProps> = ({
  onClick,
  icon,
  title,
  subtitle,
  banner,
}) => (
  <button
    onClick={onClick}
    className="group py-3 px-4 transition-all duration-300 cursor-pointer hover:bg-grey-50/80 dark:hover:bg-grey-800/80 relative w-full border border-grey-200 dark:border-grey-700 overflow-hidden bg-cover bg-center"
    style={{ backgroundImage: `url(/${banner}.jpg)` }}
  >
    <div className="absolute inset-0 bg-white/90 dark:bg-grey-900/90 group-hover:bg-white/85 dark:group-hover:bg-grey-900/85 transition-colors duration-300"></div>
    <div className="relative flex items-center space-x-3">
      <div className="text-brand-primary group-hover:text-brand-primary/80 transition-colors duration-300">
        {React.cloneElement(icon as React.ReactElement, {
          className: "w-5 h-5",
        })}
      </div>
      <div className="flex flex-col items-start">
        <h3 className="text-grey-800 dark:text-grey-100 group-hover:text-grey-900 dark:group-hover:text-white transition-colors duration-300 text-lg font-garamond">
          {title}
        </h3>
      </div>
    </div>
  </button>
);

interface DailyMeditationCardProps {
  meditation: DailyMeditationSummary | null;
  isLoading: boolean;
  onPlay: () => void;
  onPreviousDailyMeditations: () => void;
}

const DailyMeditationCard: React.FC<DailyMeditationCardProps> = ({
  meditation,
  isLoading,
  onPlay,
  onPreviousDailyMeditations,
}) => (
  <section className="relative overflow-hidden border border-grey-200 bg-cover bg-center dark:border-grey-700">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: "url(/banner-2.jpg)" }}
    />
    <div className="absolute inset-0 bg-white/95 dark:bg-grey-900/95" />
    <div className="relative flex items-center justify-between gap-4 p-4">
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-1 text-brand-primary">
          <SunIcon className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-garamond text-base text-grey-600 dark:text-grey-300">
            Daily Meditation
          </p>
          <h2 className="mt-1 font-garamond text-xl leading-tight text-grey-900 dark:text-grey-100">
            {isLoading
              ? "Loading today's meditation..."
              : meditation?.title || "Today's meditation"}
          </h2>
          {meditation?.author && (
            <p className="mt-1 font-garamond text-sm italic text-grey-600 dark:text-grey-400">
              by {meditation.author}
            </p>
          )}
        </div>
      </div>
      <button
        onClick={onPlay}
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white transition-colors duration-200 hover:bg-brand-primary/90"
        aria-label="Play daily meditation"
      >
        <span
          className="ml-1 h-0 w-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-white"
          aria-hidden="true"
        />
      </button>
    </div>
    <button
      onClick={onPreviousDailyMeditations}
      className="relative flex w-full items-center justify-between border-t border-grey-200/80 px-4 py-3 text-left font-garamond text-sm text-grey-600 transition-colors duration-200 hover:text-brand-primary dark:border-grey-700/80 dark:text-grey-300 dark:hover:text-brand-primary"
    >
      <span>Previous Daily Meditations</span>
      <ChevronRightIcon className="h-4 w-4" />
    </button>
  </section>
);

interface IntroductorySeriesCardProps {
  series: MeditationSeries | null;
  onOpen: () => void;
}

const getIntroductorySeriesCardTitle = (series: MeditationSeries | null) => {
  const meditations = series?.meditations ?? [];
  const nextPendingIndex = meditations.findIndex(
    meditation => !meditation.playedAt
  );

  if (nextPendingIndex >= 0) {
    const nextPendingMeditation = meditations[nextPendingIndex];
    const position = nextPendingMeditation.position ?? nextPendingIndex + 1;
    return `${position}. ${nextPendingMeditation.title}`;
  }

  if (meditations.length > 0) {
    return "Series Complete";
  }

  return "Twelve Guided Sessions";
};

const IntroductorySeriesCard: React.FC<IntroductorySeriesCardProps> = ({
  series,
  onOpen,
}) => {
  const completed = series?.meditations?.filter(meditation =>
    Boolean(meditation.playedAt)
  ).length ?? 0;
  const total = series?.meditations?.length || series?.meditation_count || 12;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const title = getIntroductorySeriesCardTitle(series);

  return (
    <section className="relative overflow-hidden border border-grey-200 bg-cover bg-center dark:border-grey-700">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url(/banner-3.jpg)" }}
      />
      <div className="absolute inset-0 bg-white/95 dark:bg-grey-900/95" />
      <div className="relative flex items-center justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-1 text-brand-primary">
            <BookOpenIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-garamond text-base text-grey-600 dark:text-grey-300">
              Introductory Series
            </p>
            <h2 className="mt-1 break-words font-garamond text-xl leading-tight text-grey-900 dark:text-grey-100">
              {title}
            </h2>
          </div>
        </div>
        <button
          onClick={onOpen}
          className="-mr-1 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white transition-colors duration-200 hover:bg-brand-primary/90"
          aria-label="Open introductory series"
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      </div>
      <button
        onClick={onOpen}
        className="relative w-full border-t border-grey-200/80 px-4 py-3 text-left transition-colors duration-200 hover:text-brand-primary dark:border-grey-700/80 dark:hover:text-brand-primary"
      >
        <div className="mb-2 font-garamond text-sm text-grey-600 dark:text-grey-300">
          <span>{completed} out of {total} played</span>
        </div>
        <div
          className="h-0.5 bg-grey-200 dark:bg-grey-800"
          role="progressbar"
          aria-label={`Introductory series progress: ${completed} of ${total} meditations played`}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={completed}
        >
          <div
            className="h-full bg-brand-primary transition-[width] duration-300 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </button>
    </section>
  );
};

/**
 * Home Component
 *
 * The main landing page of the application showing meditation and teaching options.
 * Displays personalised content based on user authentication status and preferences.
 *
 * @returns The rendered home page with menu options and navigation tabs
 */
const GUEST_EMAIL = "a28d3d4c-3fb2-492a-abb7-8c9c2edcd547-guest@rupertspia.com";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user, showPreviewFeatures } = useAuth();
  const isGuestUser = user?.email === GUEST_EMAIL;
  const [dailyMeditation, setDailyMeditation] =
    useState<DailyMeditationSummary | null>(null);
  const [isDailyMeditationLoading, setIsDailyMeditationLoading] =
    useState(false);
  const [introductorySeries, setIntroductorySeries] =
    useState<MeditationSeries | null>(null);
  // No longer need activeTab state as we're showing all sections

  useEffect(() => {
    // Set up notification click handler
    notificationManager.setupClickListener(() => {
      navigate("/daily");
    });

    // Cleanup listener on unmount
    return () => {
      notificationManager.removeClickListener();
    };
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;

    const fetchDailyMeditation = async () => {
      setIsDailyMeditationLoading(true);
      try {
        const summary = await meditationService.getDailyMeditationSummary();
        if (isMounted) {
          setDailyMeditation(summary);
        }
      } catch (error) {
        console.error("Error loading daily meditation summary:", error);
      } finally {
        if (isMounted) {
          setIsDailyMeditationLoading(false);
        }
      }
    };

    fetchDailyMeditation();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchIntroductorySeries = async () => {
      try {
        const series = await meditationService.getMeditationSeriesById(
          1,
          abortController.signal
        );
        if (isMounted) {
          setIntroductorySeries(series);
        }
      } catch (error) {
        if (!(error instanceof Error && error.name === "AbortError")) {
          console.error("Error loading introductory series progress:", error);
        }
      }
    };

    fetchIntroductorySeries();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, []);

  const menuItems = {
    meditation: [
      ...(user?.preview_v2_enabled && showPreviewFeatures
        ? [
          {
            title: "Personalised",
            subtitle: "AI-generated for you",
            icon: <SparklesIcon className="w-5 h-5" />,
            onClick: () => navigate("/meditate"),
            banner: "banner-5",
          },
        ]
        : []),
    ],
    teaching: [
      ...(user?.preview_v2_enabled && showPreviewFeatures
        ? [
          {
            title: "Ask a Question",
            subtitle: "Get personalised guidance",
            icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />,
            onClick: () => navigate("/chat"),
            banner: "banner-5",
          },
        ]
        : []),
      ...(user?.preview_v2_enabled && showPreviewFeatures
        ? [
          {
            title: "Q&A Library",
            subtitle: "Browse all Q&As and AI Search",
            icon: <SpeakerWaveIcon className="w-5 h-5" />,
            onClick: () => navigate("/qa-library"),
            banner: "banner-1",
          },
        ]
        : []),
      // {
      //   title: 'Publications',
      //   subtitle: 'Books and Articles',
      //   icon: <BookOpenIcon className="w-5 h-5" />,
      //   onClick: () => navigate('/publications'),
      //   banner: 'banner-4'
      // }
    ],
    readings: [
      ...(user?.preview_v2_enabled && showPreviewFeatures
        ? [
          {
            title: "Podcast",
            subtitle: "Audio conversations with Rupert",
            icon: <SpeakerWaveIcon className="w-5 h-5" />,
            onClick: () => navigate("/podcast"),
            banner: "banner-1",
          },
        ]
        : []),
      ...(user?.preview_v2_enabled && showPreviewFeatures
        ? [
          {
            title: "Sacred Texts",
            subtitle: "Read by Rupert Spira",
            icon: <PencilIcon className="w-5 h-5" />,
            onClick: () => navigate("/sacred-texts"),
            banner: "banner-2",
          },
        ]
        : []),
      ...(user?.preview_v2_enabled && showPreviewFeatures
        ? [
          {
            title: "Poetry",
            subtitle: "Read by Rupert Spira",
            icon: <PencilIcon className="w-5 h-5" />,
            onClick: () => navigate("/poetry"),
            banner: "banner-3",
          },
        ]
        : []),
      // {
      //   title: "Saved Recordings",
      //   subtitle: "Bookmarks and Listening History",
      //   icon: <BookmarkIcon className="w-5 h-5" />,
      //   onClick: () => navigate("/saved-recordings"),
      //   banner: "banner-4",
      // },
    ],
  };

  return (
    <MobileLayout>
      <div className="p-6 max-w-2xl mx-auto space-y-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-garamond text-brand-primary mb-3">
            Welcome
            {isGuestUser ? "" : user?.name ? `, ${user.name.split(" ")[0]}` : " to Luminous"}
          </h1>
          <div className="mt-2">
            <div className="flex items-center justify-center space-x-8">
              <Link
                to="/about"
                className="group flex items-center space-x-2 text-grey-500 dark:text-grey-300 hover:text-brand-primary dark:hover:text-brand-primary transition-colors duration-300 font-garamond"
              >
                <span>About Luminous</span>
                <ChevronRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
          {isGuestUser && (
            <div className="mt-6 px-2 text-center max-w-xl mx-auto space-y-4 text-gray-600 dark:text-grey-300 font-garamond text-sm leading-relaxed">
              <p>
                Luminous offers a free, short guided meditation each day. You are invited to listen to any meditation in the Introductory Series below.
              </p>
              <p>
                Create an account when you would like daily meditations to arrive automatically, along with access to a rolling seven-day library.
              </p>
            </div>
          )}
        </div>

        {/* No tab navigation - sections will be displayed inline */}

        {/* Content area with inline sections */}
        <div className="mt-8">
          <div className="grid grid-cols-1 gap-3">
            <DailyMeditationCard
              meditation={dailyMeditation}
              isLoading={isDailyMeditationLoading}
              onPlay={() => navigate("/daily")}
              onPreviousDailyMeditations={() => navigate("/meditation-library")}
            />
            <IntroductorySeriesCard
              series={introductorySeries}
              onOpen={() => navigate("/introduction")}
            />
          </div>

          {/* Meditation Section */}
          <>
            {menuItems.meditation.length > 0 && (
              <div
                className={
                  user?.preview_v2_enabled && showPreviewFeatures ? "mt-6 mb-8" : "mt-6 mb-3"
                }
              >
                {user?.preview_v2_enabled && showPreviewFeatures && (
                  <h2 className="text-2xl font-garamond text-brand-primary mb-4 border-b border-grey-200 dark:border-grey-700 pb-2">
                    Meditation
                  </h2>
                )}
                <div className="grid grid-cols-1 gap-3">
                  {menuItems.meditation.map((item, itemIndex) => (
                    <MenuCard
                      key={`meditation-${itemIndex}`}
                      onClick={item.onClick}
                      icon={item.icon}
                      title={item.title}
                      subtitle={item.subtitle}
                      banner={item.banner}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Q&A Section - Only visible if preview_v2_enabled and showPreviewFeatures is true */}
            {user?.preview_v2_enabled &&
              showPreviewFeatures &&
              menuItems.teaching.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-garamond text-brand-primary mb-4 border-b border-grey-200 dark:border-grey-700 pb-2">
                    Q&A
                  </h2>
                  <div className="grid grid-cols-1 gap-3">
                    {menuItems.teaching.map((item, itemIndex) => (
                      <MenuCard
                        key={`teaching-${itemIndex}`}
                        onClick={item.onClick}
                        icon={item.icon}
                        title={item.title}
                        subtitle={item.subtitle}
                        banner={item.banner}
                      />
                    ))}
                  </div>
                </div>
              )}

            {/* Readings Section */}
            {menuItems.readings.length > 0 && (
              <div className="mb-8">
                {user?.preview_v2_enabled && showPreviewFeatures && (
                  <h2 className="text-2xl font-garamond text-brand-primary mb-4 border-b border-grey-200 dark:border-grey-700 pb-2">
                    More
                  </h2>
                )}
                <div className="grid grid-cols-1 gap-3">
                  {menuItems.readings.map((item, itemIndex) => (
                    <MenuCard
                      key={`readings-${itemIndex}`}
                      onClick={item.onClick}
                      icon={item.icon}
                      title={item.title}
                      subtitle={item.subtitle}
                      banner={item.banner}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
          {/* Settings and Feedback Links */}
          <div className="mt-8 flex justify-center space-x-6">
            <Link
              to="/account"
              className="flex items-center gap-2 text-gray-500 dark:text-grey-300 hover:text-brand-primary dark:hover:text-brand-primary transition-colors duration-200 py-2 px-4"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span className="font-garamond">Settings</span>
            </Link>
            <Link
              to="/feedback"
              className="flex items-center gap-2 text-gray-500 dark:text-grey-300 hover:text-brand-primary dark:hover:text-brand-primary transition-colors duration-200 py-2 px-4"
            >
              <ChatBubbleOvalLeftIcon className="w-5 h-5" />
              <span className="font-garamond">Feedback</span>
            </Link>
          </div>

          {/* Beta Survey Banner */}
          {/* <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="text-sm font-medium text-amber-800 mb-1">Help us improve Luminous</div>
            <p className="text-xs text-amber-700 mb-2">Share your feedback in our beta survey</p>
            <a 
              href="https://docs.google.com/forms/d/19vCU4MX9TreK8lJwT8gmM2EldbVHZBbnGdGg3FQKE7M/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block text-xs bg-amber-600 hover:bg-amber-700 text-white py-1 px-3 rounded transition-colors duration-200"
            >
              Take Survey
            </a>
          </div> */}
        </div>
      </div>
    </MobileLayout>
  );
};

/**
 * App Component
 *
 * Root component that sets up the application's routing structure and providers.
 * Wraps the entire application with necessary context providers and defines all routes.
 *
 * @returns The complete application with all routes and navigation structure
 */
const App: React.FC = () => {
  useEffect(() => {
    // Initialize fetch interceptor to handle 401 responses
    setupFetchInterceptor();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <SplashScreenHandler />
        <LanguageProvider>
          <ThemeProvider>
            <PlaybackProvider>
              <ScrollToTop />
              <div className="min-h-screen dark:bg-grey-900 dark:text-grey-100 pb-20">
                <Suspense fallback={<RouteLoadingFallback />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/reset-password" element={<PasswordReset />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/publications" element={<Publications />} />
                    <Route
                      path="/admin/dashboard"
                      element={<Navigate to="/admin/dashboard/meditations" replace />}
                    />
                    <Route
                      path="/admin/dashboard/analytics"
                      element={
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin-dashboard"
                      element={<Navigate to="/admin/dashboard/meditations" replace />}
                    />
                    <Route
                      path="/admin/dashboard/meditations"
                      element={
                        <AdminRoute>
                          <AdminMeditationsDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/dashboard/meditations/new"
                      element={
                        <AdminRoute>
                          <AdminMeditationEdit />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/dashboard/meditations/:meditationId"
                      element={
                        <AdminRoute>
                          <AdminMeditationDetail />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/dashboard/meditations/:meditationId/edit"
                      element={
                        <AdminRoute>
                          <AdminMeditationEdit />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/feedback"
                      element={
                        <AdminRoute>
                          <AdminFeedbackInbox />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <AdminRoute>
                          <AdminUserDirectory />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/users/:userId"
                      element={
                        <AdminRoute>
                          <AdminUserDetail />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/search"
                      element={
                        <ProtectedRoute>
                          <SearchView />
                        </ProtectedRoute>
                      }
                    />

                    {/* Protected Routes */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Home />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/meditate"
                      element={
                        <ProtectedRoute>
                          <ConfigureMeditation />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/meditation/:id" element={<MeditationView />} />
                    <Route
                      path="/chat"
                      element={
                        <ProtectedRoute>
                          <AiChat />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/media"
                      element={
                        <ProtectedRoute>
                          <MediaLibrary />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/introduction"
                      element={
                        <ProtectedRoute>
                          <IntroductoryMeditations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/qa-library"
                      element={
                        <ProtectedRoute>
                          <QALibrary />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/saved-recordings"
                      element={
                        <ProtectedRoute>
                          <SavedRecordings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/sacred-texts"
                      element={
                        <ProtectedRoute>
                          <SacredTextsSeries />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/poetry"
                      element={
                        <ProtectedRoute>
                          <PoetryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/meditation-library"
                      element={
                        <ProtectedRoute>
                          <MeditationLibrary />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/podcast"
                      element={
                        <ProtectedRoute>
                          <PodcastSeries />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/feedback"
                      element={
                        <ProtectedRoute>
                          <Feedback />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/feedback/:id"
                      element={
                        <ProtectedRoute>
                          <FeedbackView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/meditation-loading/:hash?"
                      element={
                        <ProtectedRoute>
                          <MeditationLoading />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/account"
                      element={
                        <ProtectedRoute>
                          <Account />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/daily"
                      element={
                        <ProtectedRoute>
                          <DailyRedirect />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch-all route - redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
                {/* AppStoreBanner and UpdatePrompt moved outside min-h-screen div to be below MiniPlayer */}
              </div>
              <MiniPlayer />
              <AppStoreBanner />
              <UpdatePrompt />
            </PlaybackProvider>
          </ThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
